import moment from 'moment';
import {
  BrandHealthAction,
  ShopIntegrationType,
} from '../../types/apollo/generated/types.generated';
import { IBrandHealthAction } from '../../types/contentful/types.generated';

export type BrandHealthActionKey = IBrandHealthAction['key'];

export enum AdminPageTab {
  Home = 'home',
  MarketplaceDashboard = 'marketplace-dashboard',
  TradeInDashboard = 'trade-in-dashboard',
  Integrations = 'integrations',
  Disbursements = 'disbursements',
  Reports = 'reports',
  PayoutsDashboard = 'payouts-dashboard',
  InventoryUpload = 'inventory-upload',
  Feedback = 'give-feedback',
  SchedulePromotion = 'schedule-promotion',
  Billing = 'billing',
  HelpCenter = 'help-center',
}

export enum IntegrationPane {
  Description = 'description',
  Settings = 'settings',
}

export enum ShopIntegrationTypePathId {
  LoyaltyLionRewards = 'loyalty-lion-rewards',
  FillogicReturns = 'fillogic-returns',
  GivexCredit = 'givex-credit',
  HappyreturnsReturns = 'happy-returns-returns',
  KlaviyoCustomerSegmentation = 'klaviyo-customer-segmentation',
  KlaviyoEmailCustomization = 'klaviyo-email-customization',
  LoopReturns = 'loop-returns',
  ShopifyCustomerSegmentation = 'shopify-customer-segmentation',
  YotpoRewards = 'yotpo-rewards',
  RiseaiCredit = 'rise-ai-credit',
}

export const HomePageId: Record<string, BrandHealthActionKey> = {
  PostPurchaseEmails: 'post-purchase-emails',
  BrandDirectUpload: 'brand-direct-upload',
  ListReturns: 'list-returns',
  OrderNotificationLink: 'order-notification-link',
  SellerCreditPromotion: 'seller-credit-promotion',
  ResaleValueApp: 'resale-value-app',
  ReturnsPortalLink: 'returns-portal-link',
  AddTradeIn: 'add-trade-in',
  BrandDirectSale: 'brand-direct-sale',
  MainSiteLink: 'main-site-link',
  PeerListingsSale: 'peer-listings-sale',
};

export enum ActionCategory {
  Inventory = 'INVENTORY',
  Promotion = 'PROMOTION',
}

export enum ActionImpact {
  Low = 'LOW',
  Medium = 'MEDIUM',
  High = 'HIGH',
}

export enum ActionTime {
  Low = '< 1 MINUTE',
  Medium = '5-10 MINUTES',
  High = '< 1 DAY',
}

export const SHOP_INTEGRATION_TYPE_TO_PATH_ID: { [key in ShopIntegrationType]: string } = {
  [ShopIntegrationType.LoyaltylionRewards]: ShopIntegrationTypePathId.LoyaltyLionRewards,
  [ShopIntegrationType.FillogicReturns]: ShopIntegrationTypePathId.FillogicReturns,
  [ShopIntegrationType.GivexCredit]: ShopIntegrationTypePathId.GivexCredit,
  [ShopIntegrationType.HappyreturnsReturns]: ShopIntegrationTypePathId.HappyreturnsReturns,
  [ShopIntegrationType.KlaviyoCustomerSegmentation]:
    ShopIntegrationTypePathId.KlaviyoCustomerSegmentation,
  [ShopIntegrationType.KlaviyoEmailCustomization]:
    ShopIntegrationTypePathId.KlaviyoEmailCustomization,
  [ShopIntegrationType.LoopReturns]: ShopIntegrationTypePathId.LoopReturns,
  [ShopIntegrationType.ShopifyCustomerSegmentation]:
    ShopIntegrationTypePathId.ShopifyCustomerSegmentation,
  [ShopIntegrationType.YotpoRewards]: ShopIntegrationTypePathId.YotpoRewards,
  [ShopIntegrationType.RiseaiCredit]: ShopIntegrationTypePathId.RiseaiCredit,
};

export const RETOOL_EMBED_TABS = [
  AdminPageTab.MarketplaceDashboard,
  AdminPageTab.TradeInDashboard,
  AdminPageTab.PayoutsDashboard,
];

export const formatDisbursementDate = (date: Date, dateFormat?: string) =>
  moment.utc(date).format(dateFormat || 'YYYY-MM-DD');

const IMPACT_ORDER = {
  [ActionImpact.High]: 3,
  [ActionImpact.Medium]: 2,
  [ActionImpact.Low]: 1,
};

const TIME_ORDER = {
  [ActionTime.Low]: 3,
  [ActionTime.Medium]: 2,
  [ActionTime.High]: 1,
};

export const sortByCategoryImpactAndTime = (
  rows: BrandHealthAction[],
  categoryToPrioritize: ActionCategory | null
) =>
  [...rows].sort((a, b) => {
    // First sort by category to prioritize
    if (categoryToPrioritize) {
      const aHasCategoryToPrioritize = a.categories.includes(categoryToPrioritize);
      const bHasCategoryToPrioritize = b.categories.includes(categoryToPrioritize);
      if (aHasCategoryToPrioritize && !bHasCategoryToPrioritize) return -1;
      if (!aHasCategoryToPrioritize && bHasCategoryToPrioritize) return 1;
    }

    // Then sort by impact
    const impactDiff =
      IMPACT_ORDER[b.impact as ActionImpact] - IMPACT_ORDER[a.impact as ActionImpact];
    if (impactDiff !== 0) return impactDiff;

    // Finally sort by time
    return TIME_ORDER[b.time as ActionTime] - TIME_ORDER[a.time as ActionTime];
  });

export enum ActionListHeaderText {
  RecommendedActions = 'Recommended Actions',
  CompletedActions = 'Completed Actions',
  Category = 'Category',
  TimeItTakes = 'Time it Takes',
  CompletedAt = 'Completed At',
}
